import LeaderboardEmptyState from "@/components/dashboard/leaderboards/LeaderboardEmptyState";
import LeaderboardSkeleton from "@/components/dashboard/leaderboards/LeaderboardSkeleton";
import LeaderboardTable from "@/components/dashboard/leaderboards/LeaderboardTable";
import { SeasonSelectDropdown } from "@/components/dashboard/leaderboards/SeasonSelectDropdown";
import { useLeaderboards } from "@/hooks/leaderboards/useLeaderboards";
import { convertLetters } from "@/utils/letterConversion";
import { useEffect, useState } from "react";

const PAGE_SIZE = 50;

const SeasonsPage = () => {
    const [previousSeasonOffset, setPreviousSeasonOffset] = useState(0);
    const [currentSeasonOffset, setCurrentSeasonOffset] = useState(0);
    const [selectedPreviousSeasonId, setSelectedPreviousSeasonId] = useState<
        string | undefined
    >(undefined);

    const {
        topPlayersCurrentSeason,
        topPlayersPreviousSeason,
        allSeasons,
        loading,
        error,
        currentSeasonQuery,
        previousSeasonQuery,
    } = useLeaderboards({
        selectedSeasonId: selectedPreviousSeasonId,
        previousSeasonOffset,
        currentSeasonOffset,
        limit: PAGE_SIZE,
    });

    const previousSeasons = allSeasons.filter((s) => !s.isActive);

    useEffect(() => {
        if (!selectedPreviousSeasonId && previousSeasons.length > 0) {
            setSelectedPreviousSeasonId(previousSeasons[0].id);
        }
    }, [previousSeasons]);

    const handleLoadMoreCurrentSeason = () => {
        currentSeasonQuery.fetchMore({
            variables: {
                offset: currentSeasonOffset + PAGE_SIZE,
                limit: PAGE_SIZE,
            },
        });
        setCurrentSeasonOffset((prev) => prev + PAGE_SIZE);
    };

    const handleLoadMorePreviousSeason = () => {
        if (!selectedPreviousSeasonId) return;
        previousSeasonQuery.fetchMore({
            variables: {
                seasonId: selectedPreviousSeasonId,
                offset: previousSeasonOffset + PAGE_SIZE,
                limit: PAGE_SIZE,
            },
        });
        setPreviousSeasonOffset((prev) => prev + PAGE_SIZE);
    };

    if (loading && previousSeasonOffset === 0 && currentSeasonOffset === 0) {
        return <LeaderboardSkeleton />;
    }

    if (error) {
        return (
            <LeaderboardEmptyState message="Failed to load season leaderboards." />
        );
    }

    return (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Current Season Column */}
            <div className="border-brand rounded-xl border bg-zinc-900/55 p-4 sm:p-5">
                <div className="mb-2 flex min-h-14 justify-center md:items-center">
                    <h2 className="font-prviking text-brand text-center text-2xl">
                        {convertLetters("Current Season")}
                    </h2>
                </div>
                {topPlayersCurrentSeason.length > 0 ? (
                    <>
                        <LeaderboardTable players={topPlayersCurrentSeason} />
                        <div className="mt-4 flex justify-center">
                            <button
                                onClick={handleLoadMoreCurrentSeason}
                                className="font-prviking bg-brand text-brand_charcoal border-brand hover:bg-brand/80 rounded-lg border px-4 py-2 transition"
                            >
                                {convertLetters("Load More")}
                            </button>
                        </div>
                    </>
                ) : (
                    <LeaderboardEmptyState message="No data for current season." />
                )}
            </div>

            {/* Previous Season Column */}
            <div className="border-brand rounded-xl border bg-zinc-900/55 p-4 sm:p-5">
                <div className="mb-2 flex min-h-14 justify-center md:items-center">
                    <SeasonSelectDropdown
                        seasons={previousSeasons}
                        selectedSeasonId={selectedPreviousSeasonId ?? ""}
                        onSeasonChange={(v) => {
                            setSelectedPreviousSeasonId(v);
                            setPreviousSeasonOffset(0); // Reset offset when switching
                        }}
                    />
                </div>
                {topPlayersPreviousSeason.length > 0 ? (
                    <>
                        <LeaderboardTable players={topPlayersPreviousSeason} />
                        <div className="mt-4 flex justify-center">
                            <button
                                onClick={handleLoadMorePreviousSeason}
                                className="font-prviking bg-brand text-brand_charcoal border-brand hover:bg-brand/80 rounded-lg border px-4 py-2 transition"
                            >
                                {convertLetters("Load More")}
                            </button>
                        </div>
                    </>
                ) : (
                    <LeaderboardEmptyState message="No data for selected season." />
                )}
            </div>
        </div>
    );
};

export default SeasonsPage;
